import * as React from "react";

import {useIsMounted} from "@khanacademy/wonder-blocks-core";
import {usePrefersReducedMotion} from "@khan/data-prefers-reduced-motion";
import {Errors, KAError} from "@khan/logging";

interface Props {
    isExpanded: boolean;
    children?: React.ReactNode;
}

interface State {
    height: number | "auto";
    opacity: number;
    position: "static" | "absolute";
    enableTransition: boolean;
    showChildren: boolean;
}

/**
 * Expandable Component can expand and collapse.  The height of the children is
 * dynamically calculated before each transition so it can support child
 * elements that are added or removed.
 * Children are removed from DOM after collapse transition has completed
 */
const Expandable = (props: Props): React.ReactNode => {
    const {isExpanded, children} = props;

    const [state, setState] = React.useState<State>(() => ({
        showChildren: isExpanded,
        enableTransition: false,
        height: isExpanded ? "auto" : 0,
        opacity: isExpanded ? 1 : 0,
        position: "static",
    }));

    const isMounted = useIsMounted();
    const expanderContainer = React.useRef<HTMLDivElement>(null);
    const transitionPromise = React.useRef<Promise<void>>(Promise.resolve());
    const prefersReducedMotion = usePrefersReducedMotion();
    const isInitialMount = React.useRef(true);

    const getHeight = React.useCallback((): number => {
        return expanderContainer.current
            ? expanderContainer.current.getBoundingClientRect().height
            : 0;
    }, []);

    /** Return the transition delay, in seconds. */
    const getTransitionSpeed = React.useCallback((): number => {
        return prefersReducedMotion ? 0 : 0.3;
    }, [prefersReducedMotion]);

    const afterTransition = React.useCallback((): Promise<void> => {
        return new Promise((resolve) => {
            // TODO(jeff, WEB-1378): Use Wonder Blocks Timing API.
            // Dangling timer could fire after component unmounts!
            // eslint-disable-next-line no-restricted-syntax -- TODO(jeff, WEB-1378): Use Wonder Blocks Timing API for timeouts
            setTimeout(() => {
                resolve();
            }, getTransitionSpeed() * 1000);
        });
    }, [getTransitionSpeed]);

    const setStateAndWaitForAnimationFrames = React.useCallback(
        (newState: Partial<State>): Promise<void> => {
            return new Promise<void>((resolve, reject) => {
                if (!isMounted()) {
                    return reject(new KAError("unmounted", Errors.Internal));
                }

                setState((prevState) => ({...prevState, ...newState}));

                // TODO(jeff, WEB-1378): Use Wonder Blocks Timing API.
                // eslint-disable-next-line no-restricted-syntax -- TODO(jeff, WEB-1378): Use Wonder Blocks Timing API for animation frames
                requestAnimationFrame(() => {
                    resolve();
                });
            }).catch(() => {
                // Empty catch to handle case where component has been unmounted
            });
        },
        [isMounted],
    );

    const getMaxContainerHeight = React.useCallback((): Promise<number> => {
        return new Promise((resolve) => {
            setStateAndWaitForAnimationFrames({
                height: "auto",
                opacity: 0,
                position: "absolute",
                enableTransition: false,
                showChildren: true,
            }).then(() => {
                resolve(getHeight());
            });
        });
    }, [setStateAndWaitForAnimationFrames, getHeight]);

    const expand = React.useCallback((): void => {
        let height: number;

        transitionPromise.current = transitionPromise.current
            .then(getMaxContainerHeight)
            .then((maxHeight) => (height = maxHeight))
            .then(() => {
                return setStateAndWaitForAnimationFrames({
                    height: 0,
                    opacity: 0,
                    position: "static",
                    enableTransition: false,
                });
            })
            .then(() => {
                return setStateAndWaitForAnimationFrames({
                    height,
                    opacity: 1,
                    enableTransition: true,
                });
            })
            .then(afterTransition)
            .then(() =>
                setStateAndWaitForAnimationFrames({
                    height: "auto",
                }),
            );
    }, [
        getMaxContainerHeight,
        setStateAndWaitForAnimationFrames,
        afterTransition,
    ]);

    const collapse = React.useCallback((): void => {
        const height = getHeight();

        transitionPromise.current = transitionPromise.current
            .then(() =>
                setStateAndWaitForAnimationFrames({
                    height,
                    opacity: 1,
                    position: "static",
                    enableTransition: false,
                    showChildren: true,
                }),
            )
            .then(() =>
                setStateAndWaitForAnimationFrames({
                    height: 0,
                    opacity: 0,
                    enableTransition: true,
                }),
            )
            .then(afterTransition)
            .then(() =>
                setStateAndWaitForAnimationFrames({
                    showChildren: false,
                }),
            );
    }, [getHeight, setStateAndWaitForAnimationFrames, afterTransition]);

    React.useEffect(() => {
        // Skip animation on initial mount - only animate when isExpanded changes
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        if (isExpanded) {
            expand();
        } else {
            collapse();
        }
    }, [isExpanded, expand, collapse]);

    const {showChildren, height, opacity, position, enableTransition} = state;

    const speed = getTransitionSpeed();

    const style: React.CSSProperties = {
        height,
        opacity,
        position,
        overflow: "hidden",
        ...(enableTransition && {
            transitionProperty: "height, opacity",
            transitionDuration: `${speed}s, ${speed}s`,
            transitionTimingFunction: "ease-in-out, ease-in-out",
        }),
    };

    return showChildren ? (
        <div style={style} ref={expanderContainer}>
            {children}
        </div>
    ) : null;
};

export default Expandable;
